import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import type { AxiosError } from 'axios'

import { triggerToast } from '@/components/Toast'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogPortal,
  DialogTitle,
} from '@/components/ui/dialog'
import { useDeleteUserMapping } from '@/features/user-mapping/hooks'
import { useUserMappingStore } from '@/features/user-mapping/store'

interface DeleteUserMappingDialogProps {
  currentItemCount?: number
  totalItems?: number
  page?: number
  onPageChange?: (page: number) => void
}

const DeleteUserMappingDialog: React.FC<DeleteUserMappingDialogProps> = ({
  currentItemCount = 0,
  totalItems = 0,
  page = 1,
  onPageChange,
}) => {
  const { t } = useTranslation()
  const {
    deletingUserMapping,
    deleteDialogOpen,
    setDeleteDialogOpen,
    clearDeletingUserMapping,
  } = useUserMappingStore()

  const deleteMutation = useDeleteUserMapping(
    deletingUserMapping?.mapping_id || ''
  )

  const handleOpenChange = (newOpen: boolean) => {
    setDeleteDialogOpen(newOpen)
    if (!newOpen) {
      clearDeletingUserMapping()
      deleteMutation.reset()
    }
  }

  const handleConfirm = () => {
    if (!deletingUserMapping) return

    deleteMutation.mutate(undefined, {
      onSuccess: () => {
        triggerToast('success', t('userMapping.dialog.messages.deleteSuccess'))
        setDeleteDialogOpen(false)
        clearDeletingUserMapping()

        // If we're deleting the last item on a page (but not page 1), navigate to previous page
        if (currentItemCount === 1 && page > 1 && totalItems > 1) {
          onPageChange?.(page - 1)
        }
      },
      onError: (error: unknown) => {
        const axiosError = error as AxiosError<{ message?: string }>
        const errorMessage =
          axiosError.response?.data?.message ||
          axiosError.message ||
          t('userMapping.dialog.messages.errorGeneric')

        triggerToast('error', errorMessage)
      },
    })
  }

  const isDeleting = useMemo(
    () => deleteMutation.isPending,
    [deleteMutation.isPending]
  )

  return (
    <Dialog open={deleteDialogOpen} onOpenChange={handleOpenChange}>
      <DialogPortal>
        <DialogContent className="sm:max-w-[480px] bg-white shadow-xl rounded-2xl p-6">
          <DialogHeader className="space-y-0.5">
            <DialogTitle className="m-0 text-lg font-semibold text-primary-900">
              {t('userMapping.dialog.deleteTitle')}
            </DialogTitle>
            <DialogDescription className="text-sm text-tertiary-600">
              {t('userMapping.dialog.deleteDescription')}
            </DialogDescription>
          </DialogHeader>

          <DialogFooter className="flex justify-stretch !space-x-0 gap-lg pt-3">
            <Button
              type="button"
              className="flex-1 rounded-md py-2.5 px-xl font-semibold text-secondary-700 hover:text-secondary-hover text-md border-solid border-primary bg-white hover:bg-primary-hover"
              variant="default"
              onClick={() => handleOpenChange(false)}
              disabled={isDeleting}
            >
              {t('common.cancel')}
            </Button>
            <Button
              type="button"
              className="flex-1 rounded-md py-2.5 px-xl font-semibold text-md border-solid border-transparent !text-white !bg-error-solid disabled:!bg-disabled disabled:!text-disabled"
              variant="default"
              onClick={handleConfirm}
              disabled={isDeleting}
            >
              {t('common.delete')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </DialogPortal>
    </Dialog>
  )
}

export default DeleteUserMappingDialog
